/**
 * SVG assembly + the boil. The boil is two keyframes and a steps() clock;
 * the ink is swapped by re-drawing paths on hover for controls that ask
 * for it. Reduced-motion users get one static drawing, same seed.
 */

import { randomSeed, type RoughOpts } from "./core";

export interface SketchPaths {
  stroke: string[];
  fill?: string[];
}

export interface MountResult {
  svg: SVGSVGElement;
  redraw(seed?: number): void;
  destroy(): void;
}

const STYLE_ID = "ws-inline-style";

/** Injected once. Kept tiny on purpose; the rest lives in wire.css. */
export function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
.ws-svg{position:absolute;pointer-events:none;overflow:visible;left:0;top:0}
.ws-boil{animation:ws-boil-a .38s steps(2,end) infinite}
@keyframes ws-boil-a{0%{transform:translate(.35px,-.25px)}50%{transform:translate(-.3px,.3px)}100%{transform:translate(.35px,-.25px)}}
.ws-under-boil{animation:ws-boil-b .44s steps(2,end) infinite}
@keyframes ws-boil-b{0%{transform:translate(-.25px,.3px)}50%{transform:translate(.3px,-.3px)}100%{transform:translate(-.25px,.3px)}}
@media (prefers-reduced-motion: reduce){
  .ws-boil,.ws-under-boil{animation:none}
}
.ws-host{position:relative}
.ws-host--inline{display:inline-block;position:relative}
`;
  document.head.appendChild(style);
}

function reducedMotion(): boolean {
  return (
    typeof matchMedia !== "undefined" &&
    matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Build an SVG sized to the host element and lay the given paths into it.
 * The svg is absolutely positioned over the host, so the host must be
 * position:relative (we handle that in host()).
 */
export function mountSketch(
  host: HTMLElement,
  draw: (w: number, h: number, opts: RoughOpts) => SketchPaths,
  opts: RoughOpts & { boil?: boolean; under?: boolean; pad?: number } = {},
): MountResult {
  ensureStyles();
  const pad = opts.pad ?? 5;
  const boil = opts.boil !== false && !reducedMotion();
  let seed = opts.seed ?? randomSeed();

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", "ws-svg " + (boil ? (opts.under ? "ws-under-boil" : "ws-boil") : ""));
  svg.setAttribute("aria-hidden", "true");
  // "under" sketches sit on the host's own background (never behind it —
  // negative z-index would drop the ink behind an opaque host); normal
  // chrome floats above the host's content edges.
  svg.style.zIndex = opts.under ? "0" : "2";

  function redraw(nextSeed?: number): void {
    if (nextSeed !== undefined) seed = nextSeed;
    const w = Math.max(2, host.offsetWidth + pad * 2);
    const h = Math.max(2, host.offsetHeight + pad * 2);
    svg.setAttribute("width", String(w));
    svg.setAttribute("height", String(h));
    svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    const paths = draw(w, h, { seed, roughness: opts.roughness });
    for (const d of paths.fill ?? []) {
      const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
      p.setAttribute("d", d);
      p.setAttribute("fill", "none");
      p.setAttribute("stroke", "var(--ws-fill, var(--ws-ink))");
      p.setAttribute("stroke-width", "var(--ws-fill-width, 1.1)");
      p.setAttribute("opacity", "var(--ws-fill-opacity, .34)");
      p.setAttribute("stroke-linecap", "round");
      svg.appendChild(p);
    }
    for (const d of paths.stroke) {
      const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
      p.setAttribute("d", d);
      p.setAttribute("fill", "none");
      p.setAttribute("stroke", "var(--ws-ink)");
      p.setAttribute("stroke-width", "var(--ws-width, 1.6)");
      p.setAttribute("stroke-linecap", "round");
      p.setAttribute("stroke-linejoin", "round");
      svg.appendChild(p);
    }
  }

  redraw();

  svg.style.left = `-${pad}px`;
  svg.style.top = `-${pad}px`;

  // attach the ink. under-sketches go in first so the host's own
  // content stays in tree order above the fill.
  if (opts.under) host.insertBefore(svg, host.firstChild);
  else host.appendChild(svg);

  const ro = new ResizeObserver(() => redraw());
  ro.observe(host);
  const mo = new MutationObserver(() => redraw());
  mo.observe(host, { attributes: true, attributeFilter: ["style", "class"] });

  return {
    svg,
    redraw,
    destroy() {
      ro.disconnect();
      mo.disconnect();
      svg.remove();
    },
  };
}

/** Make an element a sketch host (position relative + z-stacking). */
export function host(el: HTMLElement, inline = false): void {
  el.classList.add("ws-host");
  if (inline) el.classList.add("ws-host--inline");
  const pos = getComputedStyle(el).position;
  if (pos === "static") el.style.position = "relative";
}

/** Safely find an element or throw, the way the attachers promise to. */
export function need<T extends Element>(sel: string | T | null, what: string): T {
  let el: Element | null = null;
  if (typeof sel === "string") el = document.querySelector(sel);
  else el = sel;
  if (!el) throw new Error(`wire-sketch: ${what} not found (${String(sel)})`);
  return el as T;
}

/** Find the inner field of a wrapper control, or throw. */
export function needField<T extends Element>(wrap: Element, tag: string, what: string): T {
  const field = wrap.querySelector(tag);
  if (!field) throw new Error(`wire-sketch: ${what} wrapper must contain a ${tag}`);
  return field as T;
}
